import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface SearchableSelectOption {
  value: string | number;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | number | null;
  onChange: (value: string | number | null, option: SearchableSelectOption | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  disabled,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(o => String(o.value) === String(value));

  const filtered = search
    ? options.filter(o => (o.label ?? '').toLowerCase().includes(search.toLowerCase()))
    : options;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (opt: SearchableSelectOption) => {
    onChange(opt.value, opt);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative">
      {label && <label className="block text-sm text-text-secondary mb-1.5">{label}</label>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => { setIsOpen(!isOpen); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="flex w-full items-center justify-between rounded-lg border border-border-default bg-bg-field px-3 py-2.5 text-sm text-text-primary outline-none focus:border-accent disabled:opacity-50"
      >
        <span className={selectedOption ? '' : 'text-text-muted'}>{selectedOption?.label || placeholder}</span>
        <ChevronDown className="h-4 w-4 text-text-muted shrink-0" />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-border-default bg-bg-secondary shadow-lg">
          <div className="p-2">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="w-full rounded border border-border-default bg-bg-field px-2.5 py-1.5 text-sm outline-none focus:border-accent"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-text-muted">No results</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt)}
                  className={`flex w-full px-3 py-2 text-sm text-start hover:bg-bg-hover transition-colors ${
                    String(opt.value) === String(value) ? 'bg-accent/10 text-accent font-medium' : ''
                  }`}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
